/**
 * RoasWorldTab — "Świat ROASu" container with sub-views.
 * Sub-views:
 *  1. Ścieżki klienta (Customer Paths Cohort) — top 10 purchase paths
 *  2. Lejek (Funnel Visualizer)
 *  3. Najlepsze kreacje (Best Creatives — ranked ads with preview + Hidden Gems)
 *  4. Atrybucja (placeholder)
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, BarChart3, Layers, Image } from "lucide-react";
import CustomerPathsCohort from "./customer-paths-cohort";
import FunnelVisualizer from "./funnel/funnel-visualizer";
import BestCreatives from "./best-creatives";

type RoasSubView = "paths" | "funnel" | "creatives" | "attribution";

const SUB_VIEW_CONFIG: Record<RoasSubView, { labelKey: string; icon: typeof Route }> = {
  paths: { labelKey: "dashboard.roasWorld.tabs.paths", icon: Route },
  funnel: { labelKey: "dashboard.roasWorld.tabs.funnel", icon: Layers },
  creatives: { labelKey: "dashboard.roasWorld.tabs.creatives", icon: Image },
  attribution: { labelKey: "dashboard.roasWorld.tabs.attribution", icon: BarChart3 },
};

interface RoasWorldTabProps {
  isExpertPlan: boolean;
  dashboardMode?: string;
  adAccountId?: string;
  datePreset?: string;
  customRange?: { since: string; until: string };
  forClientUserId?: string;
}

export default function RoasWorldTab({
  isExpertPlan,
  dashboardMode,
  adAccountId,
  datePreset,
  customRange,
  forClientUserId,
}: RoasWorldTabProps) {
  const { t } = useTranslation();
  const [activeSubView, setActiveSubView] = useState<RoasSubView>("paths");

  return (
    <div className="space-y-4">
      {/* Sub-view switcher */}
      <div className="flex items-center gap-1 p-1 bg-[#1a1614] border border-[#2e2624] rounded-lg w-fit">
        {(
          Object.entries(SUB_VIEW_CONFIG) as [RoasSubView, (typeof SUB_VIEW_CONFIG)[RoasSubView]][]
        ).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={key}
              onClick={() => setActiveSubView(key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                activeSubView === key
                  ? "bg-[#ef3e29] text-white"
                  : "text-white/50 hover:text-white hover:bg-white/[0.06]"
              }`}
            >
              <Icon className="w-3 h-3" />
              {t(config.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Sub-view content */}
      {activeSubView === "paths" && (
        <CustomerPathsCohort isExpertPlan={isExpertPlan} dashboardMode={dashboardMode} />
      )}

      {activeSubView === "funnel" && <FunnelVisualizer />}

      {activeSubView === "creatives" && (
        <BestCreatives
          adAccountId={adAccountId || ""}
          datePreset={datePreset || "last_30d"}
          customRange={customRange}
          forClientUserId={forClientUserId}
          dashboardMode={dashboardMode}
        />
      )}

      {activeSubView === "attribution" && (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <BarChart3 className="w-10 h-10 text-white/20 mx-auto mb-3" />
            <p className="text-sm text-white/40">
              {t("dashboard.roasWorld.attributionComingSoon")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
